import { useCallback, useEffect, useState, type FormEvent } from 'react';
import type { DepartmentSummary } from '@todo/shared';
import { ApiError } from '../api/client';
import { departmentApi } from '../api/department.api';

/**
 * Module 16 - Departments (Manage).
 *
 * Full CRUD over department data: create, rename in place, delete with a
 * confirm step. Deleting is a SOFT delete on the server - people and tasks
 * that pointed at it keep their history; their department simply reads as
 * none from then on, which the row's member/task counts let you judge before
 * you press the button.
 */
export default function DepartmentsPage() {
  const [departments, setDepartments] = useState<DepartmentSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [createBusy, setCreateBusy] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setDepartments(await departmentApi.list());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load departments');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function create(event: FormEvent) {
    event.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setCreateBusy(true);
    try {
      await departmentApi.create({ name });
      setNewName('');
      setFlash(`${name} created.`);
      void load();
    } catch (err) {
      setFlash(err instanceof ApiError ? err.message : 'Could not create the department');
    } finally {
      setCreateBusy(false);
    }
  }

  async function rename(dept: DepartmentSummary) {
    const name = editName.trim();
    if (!name || name === dept.name) {
      setEditingId(null);
      return;
    }
    setBusyId(dept.id);
    try {
      await departmentApi.update(dept.id, { name });
      setDepartments((current) =>
        current
          .map((d) => (d.id === dept.id ? { ...d, name } : d))
          .sort((a, b) => a.name.localeCompare(b.name)),
      );
      setFlash(`Renamed to ${name}.`);
      setEditingId(null);
    } catch (err) {
      setFlash(err instanceof ApiError ? err.message : 'Could not rename the department');
    } finally {
      setBusyId(null);
    }
  }

  async function remove(dept: DepartmentSummary) {
    setBusyId(dept.id);
    try {
      await departmentApi.remove(dept.id);
      setDepartments((current) => current.filter((d) => d.id !== dept.id));
      setFlash(`${dept.name} deleted. People and tasks keep their history.`);
    } catch (err) {
      setFlash(err instanceof ApiError ? err.message : 'Could not delete the department');
    } finally {
      setBusyId(null);
      setConfirmDeleteId(null);
    }
  }

  return (
    <div className="mx-auto max-w-4xl px-8 py-8">
      <header>
        <h1 className="text-xl font-semibold text-slate-900">Departments</h1>
        <p className="mt-1 text-sm text-slate-500">
          {loading ? 'Loading...' : `${departments.length} ${departments.length === 1 ? 'department' : 'departments'}`}
        </p>
      </header>

      {flash && (
        <div role="status" className="mt-5 flex items-start justify-between gap-3 rounded-lg bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-800 ring-1 ring-emerald-200">
          <span>{flash}</span>
          <button type="button" onClick={() => setFlash(null)} aria-label="Dismiss"
                  className="text-emerald-600 hover:text-emerald-800">&times;</button>
        </div>
      )}

      <form onSubmit={(e) => void create(e)} className="mt-5 flex gap-2">
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          maxLength={100}
          placeholder="New department name"
          className="min-w-0 flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none transition placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
        />
        <button type="submit" disabled={createBusy || !newName.trim()}
          className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-700 disabled:opacity-50">
          {createBusy ? 'Creating...' : 'Add department'}
        </button>
      </form>

      <div className="mt-5 overflow-hidden rounded-xl border border-slate-200 bg-white">
        {error ? (
          <div className="px-5 py-12 text-center">
            <p className="text-sm text-red-600">{error}</p>
            <button type="button" onClick={() => void load()}
              className="mt-3 rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-700 transition hover:bg-slate-50">
              Try again
            </button>
          </div>
        ) : loading ? (
          <ul className="divide-y divide-slate-100">
            {[0, 1, 2].map((i) => (
              <li key={i} className="px-5 py-4">
                <div className="h-3 w-48 animate-pulse rounded bg-slate-100" />
              </li>
            ))}
          </ul>
        ) : departments.length === 0 ? (
          <div className="px-5 py-14 text-center">
            <p className="text-sm font-medium text-slate-900">No departments yet</p>
            <p className="mt-1 text-sm text-slate-500">
              Create the first one above - it will appear in the People and Task forms.
            </p>
          </div>
        ) : (
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-5 py-2.5 font-medium">Name</th>
                <th className="px-5 py-2.5 text-right font-medium">People</th>
                <th className="px-5 py-2.5 text-right font-medium">Tasks</th>
                <th className="px-5 py-2.5 font-medium"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {departments.map((dept) => (
                <tr key={dept.id} className="transition hover:bg-slate-50/70">
                  <td className="px-5 py-3">
                    {editingId === dept.id ? (
                      <input
                        autoFocus
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') { e.preventDefault(); void rename(dept); }
                          if (e.key === 'Escape') setEditingId(null);
                        }}
                        maxLength={100}
                        className="w-full max-w-xs rounded-lg border border-brand-400 px-2.5 py-1.5 text-sm outline-none ring-2 ring-brand-100"
                      />
                    ) : (
                      <span className="font-medium text-slate-900">{dept.name}</span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums text-slate-600">{dept.memberCount}</td>
                  <td className="px-5 py-3 text-right tabular-nums text-slate-600">{dept.taskCount}</td>
                  <td className="px-5 py-3 text-right">
                    {editingId === dept.id ? (
                      <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                        <button type="button" disabled={busyId === dept.id}
                          onClick={() => void rename(dept)}
                          className="rounded-lg bg-brand-600 px-2.5 py-1 text-xs font-medium text-white transition hover:bg-brand-700 disabled:opacity-50">
                          Save
                        </button>
                        <button type="button" onClick={() => setEditingId(null)}
                          className="rounded-lg px-2 py-1 text-xs font-medium text-slate-600 transition hover:bg-slate-100">
                          Cancel
                        </button>
                      </span>
                    ) : confirmDeleteId === dept.id ? (
                      <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
                        <button type="button" disabled={busyId === dept.id}
                          onClick={() => void remove(dept)}
                          className="rounded-lg bg-red-600 px-2.5 py-1 text-xs font-medium text-white transition hover:bg-red-700 disabled:opacity-50">
                          {busyId === dept.id ? 'Deleting...' : 'Confirm'}
                        </button>
                        <button type="button" onClick={() => setConfirmDeleteId(null)}
                          className="rounded-lg px-2 py-1 text-xs font-medium text-slate-600 transition hover:bg-slate-100">
                          Keep
                        </button>
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 whitespace-nowrap">
                        <button type="button"
                          onClick={() => { setEditingId(dept.id); setEditName(dept.name); }}
                          className="rounded-lg px-2.5 py-1 text-xs font-medium text-slate-600 transition hover:bg-slate-100 hover:text-slate-900">
                          Rename
                        </button>
                        <button type="button" onClick={() => setConfirmDeleteId(dept.id)}
                          className="rounded-lg px-2.5 py-1 text-xs font-medium text-red-600 transition hover:bg-red-50">
                          Delete
                        </button>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
